import React, { useMemo, useState } from "react";
import styles from "../styles/LLMSettingsSection.module.css";
import { defaultModelFor } from "../utils/modelLists";
import { buildConfiguredProviderOptions } from "../utils/providerOptions";
import { DISABLED_PROVIDER } from "../utils/userPageHelpers";
import ModelSelect from "./subcomponents/ModelSelect";
import ProfileSwitcher from "./subcomponents/ProfileSwitcher";

const PARAM_DESCRIPTIONS = {
  provider: "The orchestrator provider to use.",
  model: "The orchestrator model to use for orchestrating agents.",
  agentModels: "Agent models are only applicable for Stackform confirmed agents (e.g., Researcher Agent)."
};

// Gray styling for the "Select ..." placeholder options so the empty
// (nothing-chosen-yet) state reads consistently across every dropdown.
const PLACEHOLDER_STYLE = { color: '#999' };

// The left panel's model settings.  Everything comes from useModelSettings
// (App passes the hook's state down) and the model fields list its
// `modelLists`, so the panel fetches nothing of its own.
function LLMSettingsSection({
  model, setModel,
  provider, setProvider,
  agentModelAssignments = {}, setAgentModelAssignments,
  personas,
  providerConfigs = {},
  modelLists = {},
  isRefreshing = false,
  profiles = [],
  activeProfileName = 'Default',
  switchProfile,
  isModelDirty = false,
}) {
  const [showOverlay, setShowOverlay] = useState(false);
  // The provider dropdowns' options: the list Settings and the builder show.
  const providerConfigOptions = useMemo(
    () => buildConfiguredProviderOptions(providerConfigs),
    [providerConfigs],
  );
  const { localModels = {}, localModelsLoaded = false } = modelLists;

  // A local key whose device lists no models, once the models have loaded.
  const isOffline = (key) =>
    key.split('_')[0] === 'local' &&
    localModelsLoaded &&
    (localModels[key] ?? []).length === 0;

  const renderField = (label, input, keyProp) => (
    <div key={keyProp} className={styles.settingsField}>
      <label className={styles.settingsLabel}>{label}</label>
      {input}
    </div>
  );

  const handleProviderChange = (e) => {
    const newProvider = e.target.value;
    setProvider(newProvider);
    setModel(defaultModelFor(newProvider, modelLists));
  };

  return (
    <div>
      <style>
        {`
        .settings-desc-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 3000;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .settings-desc-modal {
          background: var(--modal-background);
          color: var(--text-color);
          border-radius: 8px;
          max-width: 380px;
          width: 97vw;
          padding: 0.7rem 0.4rem 0.7rem 0.7rem;
          box-shadow: 0 2px 16px var(--modal-shadow);
          position: relative;
        }
        .settings-desc-modal h3 {
          margin-top: 0;
          font-size: 1.05em;
          color: var(--heading-color);
        }
        .settings-desc-close {
          position: absolute;
          top: 8px;
          right: 8px;
          background: none;
          border: none;
          font-size: 1.5rem;
          color: var(--secondary-text-color);
          cursor: pointer;
          z-index: 10;
          line-height: 1;
          padding: 0;
        }
        `}
      </style>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <strong>Model Settings</strong>
        <button
          type="button"
          className={styles.descQmarkBtn}
          onClick={() => setShowOverlay(true)}
          aria-label="Show explanation of model settings"
        >
          <b>?</b>
        </button>
        {isRefreshing && (
          <span style={{ fontSize: "0.8em", color: "var(--secondary-text-color)", marginLeft: "auto" }}>
            Refreshing...
          </span>
        )}
      </div>
      <form style={{ marginTop: 12 }} onSubmit={(e) => e.preventDefault()}>
        {switchProfile && (
          // Select-only here: creating / duplicating / renaming / deleting
          // profiles lives solely in the Model Defaults tab (UserPage). The
          // left panel only switches which profile is active.
          <ProfileSwitcher
            profiles={profiles}
            activeProfileName={activeProfileName}
            onSwitch={switchProfile}
            isDirty={isModelDirty}
          />
        )}
        <div style={{ marginBottom: 16 }}>
            <div className={styles.settingsLabel} style={{ marginBottom: 8 }}>Main Model</div>
            {renderField(
              "Provider",
              <select value={provider} onChange={handleProviderChange}>
                <option value="default">Default</option>
                {providerConfigOptions.map(({ value, label }) => {
                  const offline = isOffline(value);
                  return (
                    <option key={value} value={value} disabled={offline}>
                      {label}{offline ? ' (offline)' : ''}
                    </option>
                  );
                })}
              </select>
            )}
            {renderField(
              "Model",
              <ModelSelect
                providerKey={provider}
                value={model}
                onChange={setModel}
                modelLists={modelLists}
                listId="model-suggestions-main"
              />
             )}
        </div>

        <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 12 }}>
             <div className={styles.settingsLabel} style={{ marginBottom: 8 }}>
               Agent Models
             </div>
             {Object.entries(agentModelAssignments)
                 .sort(([a], [b]) => a.localeCompare(b))
                 .map(([agentName, assignment]) => {
                   const isDefaultAgent = ['aeri', 'capo', 'cardian'].includes(agentName);
                   const providerKey = assignment?.provider || (isDefaultAgent ? 'stackform' : '');

                   return renderField(
                     agentName.charAt(0).toUpperCase() + agentName.slice(1),
                     <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                       <select
                         value={providerKey}
                         onChange={(e) => {
                           const newProvider = e.target.value;
                           // Disabled agents carry no model; otherwise pick
                           // the provider's default model.
                           const defaultModel =
                             newProvider === DISABLED_PROVIDER
                               ? ''
                               : newProvider === 'stackform'
                                 ? 'default-rapid-model'
                                 : defaultModelFor(newProvider, modelLists);
                           setAgentModelAssignments(prev => ({
                             ...prev,
                             [agentName]: {
                               ...prev[agentName],
                               provider: newProvider,
                               model_name: defaultModel,
                             }
                           }));
                         }}
                         style={{ fontSize: '0.9em' }}
                       >
                          {!isDefaultAgent && <option value="" style={PLACEHOLDER_STYLE}>Select provider</option>}
                         {isDefaultAgent && <option value={DISABLED_PROVIDER}>None (disabled)</option>}
                         {isDefaultAgent && <option value="stackform">Stackform (default)</option>}
                         {providerConfigOptions.map(({ value, label }) => {
                            const offline = isOffline(value);
                            return (
                              <option key={value} value={value} disabled={offline}>
                                {label}{offline ? ' (offline)' : ''}
                              </option>
                            );
                          })}
                       </select>

                       {providerKey === DISABLED_PROVIDER ? (
                         <select value="" disabled style={{ fontSize: '0.9em' }}>
                           <option value="">Agent disabled</option>
                         </select>
                       ) : providerKey === 'stackform' ? (
                         <select value="default-rapid-model" disabled style={{ fontSize: '0.9em' }}>
                           <option value="default-rapid-model">default-rapid-model</option>
                         </select>
                       ) : !providerKey && !isDefaultAgent ? (
                          <select value="" disabled style={{ fontSize: '0.9em' }}>
                            <option value="" style={PLACEHOLDER_STYLE}>Select model</option>
                          </select>
                       ) : (
                         <ModelSelect
                           providerKey={providerKey}
                           value={assignment?.model_name || ''}
                           onChange={(modelName) => setAgentModelAssignments(prev => ({
                             ...prev,
                             [agentName]: { ...prev[agentName], model_name: modelName },
                           }))}
                           modelLists={modelLists}
                           listId={`model-suggestions-${agentName}`}
                           selectProps={{ style: { fontSize: '0.9em' } }}
                           inputProps={{ style: { fontSize: '0.9em', padding: '0.25rem' } }}
                         />
                       )}

                        <select
                          value={assignment?.persona_id || ''}
                          disabled={providerKey === DISABLED_PROVIDER}
                          onChange={(e) => {
                            const personaId = e.target.value;
                            setAgentModelAssignments(prev => ({
                              ...prev,
                              [agentName]: { ...prev[agentName], persona_id: personaId }
                            }));
                          }}
                          style={{ fontSize: '0.9em' }}
                        >
                          <option value="">No persona</option>
                          {personas && personas
                            .filter(p => p.id && p.title)
                            .map(persona => (
                              <option key={persona.id} value={persona.id}>{persona.title}</option>
                          ))}
                        </select>
                     </div>,
                     agentName
                   );
                 })}
        </div>
      </form>
      {showOverlay && (
        <div className="settings-desc-overlay" onClick={() => setShowOverlay(false)}>
          <div className="settings-desc-modal" onClick={e => e.stopPropagation()}>
            <button
              className="settings-desc-close"
              onClick={() => setShowOverlay(false)}
              aria-label="Close"
              type="button"
            >
              &times;
            </button>
            <h3>Model Settings Explained</h3>
            <p>
              Hint: Premium/bigger models are generally more knowledgeable but
              lack speed like Gemini Flash Lite, Claude Haiku, and GPT nano models.
            </p>
            <ul style={{ paddingLeft: 18, fontSize: "0.89em", marginBottom: 0 }}>
              {Object.entries(PARAM_DESCRIPTIONS).map(([key, desc]) => (
                <li key={key} style={{ marginBottom: 10 }}>
                  <b>{key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase())}:</b>
                  <br />
                  <span style={{ color: "var(--secondary-text-color)" }}>{desc}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default LLMSettingsSection;
